import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { X, Wand2, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { useToast } from '../hooks/use-toast';
import { API } from '../context/AdminAuthContext';
import { useAdminPermissions } from '../hooks/useAdminPermissions';
import Rial from './Rial';

/**
 * BulkProductEdit
 *
 * Two-step dialog for editing many products at once:
 *   1. Field-by-field form. Each row has an "Enable" switch — only enabled
 *      fields are sent to the backend. This keeps the surface area minimal
 *      ("no, I don't accidentally want to zero out everyone's tag") and
 *      makes the operation idempotent.
 *   2. Preview confirmation step. Shows exactly which fields are being
 *      changed and the number of products affected, so the admin commits
 *      with full context — prevents the "I bulk-edited 600 SKUs to 0 OMR"
 *      class of mistake.
 */
const FIELD_META = [
  { key: 'category_slug', label: 'Category', kind: 'category' },
  { key: 'price_mode',    label: 'Price',    kind: 'price' },
  { key: 'min_qty',       label: 'Min order qty', kind: 'number_min1' },
  { key: 'uom',           label: 'UOM',      kind: 'text' },
  { key: 'in_stock',      label: 'In stock', kind: 'bool' },
  { key: 'vat_applicable', label: 'VAT applicable (5%)', kind: 'bool' },
  { key: 'tag',           label: 'Tag',      kind: 'text' },
];

const initialFields = () => ({
  category_slug: { on: false, value: '' },
  price_mode:    { on: false, mode: 'absolute', value: '', percent: '' }, // mode: absolute|percent
  min_qty:       { on: false, value: 1 },
  uom:           { on: false, value: 'ctn' },
  in_stock:      { on: false, value: true },
  vat_applicable: { on: false, value: false },
  tag:           { on: false, value: '' },
  tag_color:     { on: false, value: '#0078D4' },
  size:          { on: false, value: '', type: 'size' },
  image:         { on: false, value: '' },
  name_affix:    { on: false, prefix: '', suffix: '' },
});

const BulkProductEdit = ({ open, onClose, selectedIds, categories, products, authHeaders, onApplied }) => {
  const { toast } = useToast();
  const { can } = useAdminPermissions();
  // Bulk-edit rows mirror the same `products.field_*` permissions used
  // by the Products page. A sub-admin can only see/queue changes for
  // fields whose visibility checkbox is granted — anything else is
  // omitted from the form AND from the build payload, so a tampered
  // client can't slip a hidden field into the bulk patch.
  const fieldVisible = {
    category:  can('products.field_category'),
    price:     can('products.field_price'),
    min_qty:   can('products.field_min_qty'),
    uom:       can('products.field_uom'),
    in_stock:  can('products.field_in_stock'),
    vat:       can('products.field_vat'),
    tag:       can('products.field_tag'),
    size:      can('products.field_size'),
    image:     can('products.field_image'),
  };
  const [fields, setFields] = useState(initialFields());
  const [step, setStep] = useState('edit'); // edit | preview
  const [saving, setSaving] = useState(false);

  const selectedProducts = useMemo(
    () => products.filter((p) => selectedIds.includes(p.id)),
    [products, selectedIds],
  );

  if (!open) return null;

  const reset = () => {
    setFields(initialFields());
    setStep('edit');
  };
  const handleClose = () => { reset(); onClose(); };

  const setField = (key, patch) => setFields((s) => ({ ...s, [key]: { ...s[key], ...patch } }));

  // Build payload for the backend, only including enabled fields the
  // current admin has visibility on. Stripping forbidden fields here
  // gives a single failure mode (silent omission) instead of a confusing
  // 403 from the API mid-save.
  const buildPayload = () => {
    const out = { ids: selectedIds };
    if (fieldVisible.category && fields.category_slug.on && fields.category_slug.value)
      out.category_slug = fields.category_slug.value;
    if (fieldVisible.price && fields.price_mode.on) {
      if (fields.price_mode.mode === 'absolute' && fields.price_mode.value !== '')
        out.price = parseFloat(fields.price_mode.value);
      else if (fields.price_mode.mode === 'percent' && fields.price_mode.percent !== '')
        out.price_percent_adjust = parseFloat(fields.price_mode.percent);
    }
    if (fieldVisible.min_qty && fields.min_qty.on) out.min_qty = Math.max(1, parseInt(fields.min_qty.value, 10) || 1);
    if (fieldVisible.uom && fields.uom.on) out.uom = (fields.uom.value || 'ctn').trim() || 'ctn';
    if (fieldVisible.in_stock && fields.in_stock.on) out.in_stock = !!fields.in_stock.value;
    if (fieldVisible.vat && fields.vat_applicable.on) out.vat_applicable = !!fields.vat_applicable.value;
    if (fieldVisible.tag && fields.tag.on) out.tag = fields.tag.value;
    if (fieldVisible.tag && fields.tag_color.on) out.tag_color = fields.tag_color.value;
    if (fieldVisible.size && fields.size.on) {
      out.size = fields.size.value;
      out.size_type = fields.size.type === 'pack' ? 'pack' : 'size';
    }
    if (fieldVisible.image && fields.image.on) out.image = fields.image.value;
    if (fields.name_affix.on && (fields.name_affix.prefix || fields.name_affix.suffix)) {
      if (fields.name_affix.prefix) out.name_prefix = fields.name_affix.prefix;
      if (fields.name_affix.suffix) out.name_suffix = fields.name_affix.suffix;
    }
    return out;
  };

  // Human summary mirrors `buildPayload` — hidden fields contribute
  // nothing so the preview matches exactly what the backend will see.
  const summary = () => {
    const lines = [];
    if (fieldVisible.category && fields.category_slug.on && fields.category_slug.value) {
      const c = categories.find((c) => c.slug === fields.category_slug.value);
      lines.push(`Move to category “${c?.name || fields.category_slug.value}”`);
    }
    if (fieldVisible.price && fields.price_mode.on) {
      if (fields.price_mode.mode === 'absolute' && fields.price_mode.value !== '')
        lines.push(`Set price to OMR ${Number(fields.price_mode.value).toFixed(3)}`);
      else if (fields.price_mode.mode === 'percent' && fields.price_mode.percent !== '') {
        const p = Number(fields.price_mode.percent);
        lines.push(`${p >= 0 ? 'Increase' : 'Decrease'} price by ${Math.abs(p)}%`);
      }
    }
    if (fieldVisible.min_qty && fields.min_qty.on) lines.push(`Set min order qty to ${fields.min_qty.value}`);
    if (fieldVisible.uom && fields.uom.on) lines.push(`Set UOM to "${fields.uom.value || 'ctn'}"`);
    if (fieldVisible.in_stock && fields.in_stock.on) lines.push(`Mark as ${fields.in_stock.value ? 'in stock' : 'out of stock'}`);
    if (fieldVisible.vat && fields.vat_applicable.on) lines.push(`${fields.vat_applicable.value ? 'Enable' : 'Disable'} 5% VAT`);
    if (fieldVisible.tag && fields.tag.on) lines.push(fields.tag.value ? `Set tag to "${fields.tag.value}"` : 'Remove tag');
    if (fieldVisible.tag && fields.tag_color.on) lines.push(`Set tag color to ${fields.tag_color.value}`);
    if (fieldVisible.size && fields.size.on) {
      const tLabel = fields.size.type === 'pack' ? 'pack' : 'size';
      lines.push(fields.size.value ? `Set ${tLabel} to "${fields.size.value}"` : `Clear ${tLabel}`);
    }
    if (fieldVisible.image && fields.image.on) lines.push(fields.image.value ? 'Replace image URL' : 'Clear image (fall back to category image)');
    if (fields.name_affix.on && (fields.name_affix.prefix || fields.name_affix.suffix)) {
      const parts = [];
      if (fields.name_affix.prefix) parts.push(`prefix "${fields.name_affix.prefix}"`);
      if (fields.name_affix.suffix) parts.push(`suffix "${fields.name_affix.suffix}"`);
      lines.push(`Add ${parts.join(' + ')} to product names`);
    }
    return lines;
  };

  const anyEnabled = Object.values(fields).some((f) => f.on);
  const lines = summary();

  const handleNext = () => {
    if (lines.length === 0) {
      toast({ title: 'Nothing to apply', description: 'Enable at least one field and set a value.' });
      return;
    }
    setStep('preview');
  };

  const handleApply = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      const r = await axios.post(`${API}/admin/products/bulk-edit`, payload, { headers: authHeaders });
      toast({
        title: 'Bulk edit applied',
        description: `${r.data.modified} of ${r.data.requested} product${r.data.requested === 1 ? '' : 's'} updated.`,
      });
      handleClose();
      onApplied?.();
    } catch (err) {
      toast({ title: 'Bulk edit failed', description: err.response?.data?.detail || 'Try again' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70]" data-testid="bulk-edit-dialog">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl flex flex-col">
        <div className="p-5 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-[#0078D4]" />
              {step === 'edit' ? 'Bulk edit' : 'Confirm changes'}
            </h3>
            <p className="text-xs text-neutral-500 mt-0.5">
              {selectedIds.length} product{selectedIds.length === 1 ? '' : 's'} selected
            </p>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-neutral-100 rounded-full" data-testid="bulk-edit-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === 'edit' && (
          <div className="flex-1 overflow-y-auto p-5 space-y-3">
            <p className="text-xs text-neutral-500">Toggle a row on, set the new value, and we'll apply only the enabled fields to every selected product.</p>

            {/* Category */}
            {fieldVisible.category && (
            <FieldRow label="Division" enabled={fields.category_slug.on} onToggle={(v) => setField('category_slug', { on: v })}>
              <select
                value={fields.category_slug.value}
                onChange={(e) => setField('category_slug', { value: e.target.value })}
                disabled={!fields.category_slug.on}
                className="mt-1 w-full h-9 px-2 rounded-md border border-neutral-300 bg-white text-sm disabled:bg-neutral-50"
                data-testid="bulk-edit-category"
              >
                <option value="">— Select division —</option>
                {categories.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.name}</option>
                ))}
              </select>
            </FieldRow>
            )}

            {/* Price */}
            {fieldVisible.price && (
            <FieldRow label="Price" enabled={fields.price_mode.on} onToggle={(v) => setField('price_mode', { on: v })}>
              <div className="space-y-2 mt-1">
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setField('price_mode', { mode: 'absolute' })}
                    disabled={!fields.price_mode.on}
                    className={`flex-1 h-8 rounded-md text-xs font-semibold border transition-colors disabled:opacity-50 ${
                      fields.price_mode.mode === 'absolute' ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-neutral-700 border-neutral-300'
                    }`}
                    data-testid="bulk-price-mode-absolute"
                  >
                    Set to <Rial />
                  </button>
                  <button
                    type="button"
                    onClick={() => setField('price_mode', { mode: 'percent' })}
                    disabled={!fields.price_mode.on}
                    className={`flex-1 h-8 rounded-md text-xs font-semibold border transition-colors disabled:opacity-50 ${
                      fields.price_mode.mode === 'percent' ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-neutral-700 border-neutral-300'
                    }`}
                    data-testid="bulk-price-mode-percent"
                  >
                    Adjust by %
                  </button>
                </div>
                {fields.price_mode.mode === 'absolute' ? (
                  <Input
                    type="number"
                    step="0.001"
                    min="0"
                    placeholder="e.g. 0.500"
                    value={fields.price_mode.value}
                    onChange={(e) => setField('price_mode', { value: e.target.value })}
                    disabled={!fields.price_mode.on}
                    data-testid="bulk-price-absolute-input"
                  />
                ) : (
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="e.g. 5 or -10"
                    value={fields.price_mode.percent}
                    onChange={(e) => setField('price_mode', { percent: e.target.value })}
                    disabled={!fields.price_mode.on}
                    data-testid="bulk-price-percent-input"
                  />
                )}
              </div>
            </FieldRow>
            )}

            {/* Min qty */}
            {fieldVisible.min_qty && (
            <FieldRow label="Min order qty" enabled={fields.min_qty.on} onToggle={(v) => setField('min_qty', { on: v })}>
              <Input
                type="number"
                min="1"
                step="1"
                value={fields.min_qty.value}
                onChange={(e) => setField('min_qty', { value: e.target.value })}
                disabled={!fields.min_qty.on}
                className="mt-1"
                data-testid="bulk-min-qty-input"
              />
            </FieldRow>
            )}

            {/* UOM */}
            {fieldVisible.uom && (
            <FieldRow label="UOM" enabled={fields.uom.on} onToggle={(v) => setField('uom', { on: v })}>
              <Input
                value={fields.uom.value}
                onChange={(e) => setField('uom', { value: e.target.value })}
                disabled={!fields.uom.on}
                placeholder="ctn"
                className="mt-1"
                data-testid="bulk-uom-input"
              />
            </FieldRow>
            )}

            {/* In stock */}
            {fieldVisible.in_stock && (
            <FieldRow label="In stock" enabled={fields.in_stock.on} onToggle={(v) => setField('in_stock', { on: v })}>
              <div className="flex items-center justify-between mt-1 p-2 bg-neutral-50 rounded-md">
                <span className="text-sm">{fields.in_stock.value ? 'Mark all as IN stock' : 'Mark all as OUT of stock'}</span>
                <Switch
                  checked={fields.in_stock.value}
                  onCheckedChange={(v) => setField('in_stock', { value: v })}
                  disabled={!fields.in_stock.on}
                  className="data-[state=checked]:bg-[#0078D4]"
                  data-testid="bulk-in-stock-switch"
                />
              </div>
            </FieldRow>
            )}

            {/* VAT */}
            {fieldVisible.vat && (
            <FieldRow label="VAT applicable (5%)" enabled={fields.vat_applicable.on} onToggle={(v) => setField('vat_applicable', { on: v })}>
              <div className="flex items-center justify-between mt-1 p-2 bg-neutral-50 rounded-md">
                <span className="text-sm">{fields.vat_applicable.value ? 'Charge 5% Oman VAT' : 'No VAT'}</span>
                <Switch
                  checked={fields.vat_applicable.value}
                  onCheckedChange={(v) => setField('vat_applicable', { value: v })}
                  disabled={!fields.vat_applicable.on}
                  className="data-[state=checked]:bg-[#0078D4]"
                  data-testid="bulk-vat-switch"
                />
              </div>
            </FieldRow>
            )}

            {/* Tag */}
            {fieldVisible.tag && (
            <FieldRow label="Tag" enabled={fields.tag.on} onToggle={(v) => setField('tag', { on: v })}>
              <Input
                value={fields.tag.value}
                onChange={(e) => setField('tag', { value: e.target.value })}
                disabled={!fields.tag.on}
                placeholder="(leave blank to clear)"
                className="mt-1"
                data-testid="bulk-tag-input"
              />
            </FieldRow>
            )}

            {/* Tag color — bundled under the `field_tag` permission since
                colour is meaningless without the tag being visible. */}
            {fieldVisible.tag && (
            <FieldRow label="Tag color" enabled={fields.tag_color.on} onToggle={(v) => setField('tag_color', { on: v })}>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={fields.tag_color.value || '#0078D4'}
                  onChange={(e) => setField('tag_color', { value: e.target.value })}
                  disabled={!fields.tag_color.on}
                  className="h-10 w-12 rounded-md border border-neutral-300 cursor-pointer disabled:opacity-40"
                  data-testid="bulk-tag-color-input"
                />
                <Input
                  value={fields.tag_color.value}
                  onChange={(e) => setField('tag_color', { value: e.target.value })}
                  disabled={!fields.tag_color.on}
                  placeholder="#RRGGBB"
                  className="flex-1"
                />
              </div>
            </FieldRow>
            )}

            {/* Size */}
            {fieldVisible.size && (
            <FieldRow
              label={fields.size.type === 'pack' ? 'Pack' : 'Size'}
              enabled={fields.size.on}
              onToggle={(v) => setField('size', { on: v })}
            >
              <div className="mt-1 flex items-stretch gap-1.5">
                <select
                  value={fields.size.type || 'size'}
                  onChange={(e) => setField('size', { type: e.target.value })}
                  disabled={!fields.size.on}
                  className="h-10 px-2 rounded-md border border-neutral-300 bg-neutral-50 text-xs font-semibold uppercase tracking-wide text-neutral-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40"
                  data-testid="bulk-size-type-select"
                  aria-label="Size or pack"
                >
                  <option value="size">Size</option>
                  <option value="pack">Pack</option>
                </select>
                <Input
                  value={fields.size.value}
                  onChange={(e) => setField('size', { value: e.target.value })}
                  disabled={!fields.size.on}
                  placeholder={fields.size.type === 'pack' ? 'e.g. 12 x 500g (blank to clear)' : 'e.g. 100ml (blank to clear)'}
                  className="flex-1"
                  data-testid="bulk-size-input"
                />
              </div>
            </FieldRow>
            )}

            {/* Image URL */}
            {fieldVisible.image && (
            <FieldRow label="Image URL" enabled={fields.image.on} onToggle={(v) => setField('image', { on: v })}>
              <Input
                value={fields.image.value}
                onChange={(e) => setField('image', { value: e.target.value })}
                disabled={!fields.image.on}
                placeholder="https://… or /api/images/{id} (blank = clear)"
                className="mt-1"
                data-testid="bulk-image-input"
              />
              <p className="text-[11px] text-neutral-500 mt-1">Tip: leave blank to clear and fall back to the division image.</p>
            </FieldRow>
            )}

            {/* Name prefix/suffix */}
            <FieldRow label="Name (prefix / suffix)" enabled={fields.name_affix.on} onToggle={(v) => setField('name_affix', { on: v })}>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Input
                  value={fields.name_affix.prefix}
                  onChange={(e) => setField('name_affix', { prefix: e.target.value })}
                  disabled={!fields.name_affix.on}
                  placeholder="Prefix (e.g. NEW - )"
                  data-testid="bulk-name-prefix-input"
                />
                <Input
                  value={fields.name_affix.suffix}
                  onChange={(e) => setField('name_affix', { suffix: e.target.value })}
                  disabled={!fields.name_affix.on}
                  placeholder="Suffix (e.g.  (Sale))"
                  data-testid="bulk-name-suffix-input"
                />
              </div>
              <p className="text-[11px] text-neutral-500 mt-1">We prepend/append to each name — never overwrite (so SKUs stay unique).</p>
            </FieldRow>
          </div>
        )}

        {step === 'preview' && (
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            <div className="bg-[#DEECF9] border border-[#0078D4]/30 rounded-lg p-4">
              <div className="text-xs font-semibold text-[#0078D4] uppercase tracking-wide mb-1">Summary</div>
              <div className="text-sm text-[#0F172A]">
                Apply <b>{lines.length}</b> change{lines.length === 1 ? '' : 's'} to <b>{selectedIds.length}</b> product{selectedIds.length === 1 ? '' : 's'}.
              </div>
            </div>

            <ul className="space-y-2">
              {lines.map((line, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#0078D4] shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>

            <div className="border-t border-neutral-200 pt-3">
              <div className="text-[11px] font-bold text-neutral-600 uppercase tracking-wide mb-2">
                Products that will be affected ({selectedProducts.length})
              </div>
              <div className="max-h-48 overflow-y-auto bg-neutral-50 rounded-md border border-neutral-200 divide-y divide-neutral-200">
                {selectedProducts.slice(0, 100).map((p) => (
                  <div key={p.id} className="px-3 py-2 text-xs flex items-center justify-between gap-2">
                    <span className="truncate font-medium">{p.name}</span>
                    <span className="text-neutral-500 shrink-0"><Rial /> {p.price.toFixed(3)}</span>
                  </div>
                ))}
                {selectedProducts.length > 100 && (
                  <div className="px-3 py-2 text-xs text-neutral-500">…and {selectedProducts.length - 100} more.</div>
                )}
              </div>
            </div>

            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>This action runs immediately and cannot be undone in one click. Double-check the summary above before applying.</span>
            </div>
          </div>
        )}

        <div className="p-5 border-t border-neutral-200 flex gap-2">
          {step === 'edit' ? (
            <>
              <Button type="button" variant="outline" onClick={handleClose} className="flex-1 h-11 rounded-full">
                Cancel
              </Button>
              <Button
                onClick={handleNext}
                disabled={!anyEnabled}
                className="flex-1 h-11 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold disabled:opacity-50"
                data-testid="bulk-edit-next"
              >
                Review changes
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={() => setStep('edit')} className="flex-1 h-11 rounded-full">
                Back
              </Button>
              <Button
                onClick={handleApply}
                disabled={saving}
                className="flex-1 h-11 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold"
                data-testid="bulk-edit-apply"
              >
                {saving ? 'Applying…' : `Apply to ${selectedIds.length}`}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const FieldRow = ({ label, enabled, onToggle, children }) => (
  <div className={`rounded-lg border p-3 transition-colors ${enabled ? 'border-[#0078D4] bg-[#F7FBFF]' : 'border-neutral-200 bg-white'}`}>
    <div className="flex items-center justify-between">
      <span className={`text-sm font-semibold ${enabled ? 'text-[#0F172A]' : 'text-neutral-500'}`}>{label}</span>
      <Switch
        checked={enabled}
        onCheckedChange={onToggle}
        className="data-[state=checked]:bg-[#0078D4]"
        data-testid={`bulk-toggle-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      />
    </div>
    {children}
  </div>
);

export default BulkProductEdit;
